// 현장 상태 전이 — `0024` 의 project_status_guard 와 같은 표다. 저장소는 여기서 정한 것만 쓴다.

import type { ContractStatus } from "../contract/types";
import { addMonthsClamped } from "./build-closeout";
import {
  PROJECT_LOG_KINDS,
  type Project,
  type ProjectLogKind,
  type ProjectPhase,
  type ProjectPhaseStatus,
  type ProjectStatus,
} from "./types";

export const PROJECT_TRANSITIONS: Record<ProjectStatus, readonly ProjectStatus[]> = {
  SCHEDULED: ["IN_PROGRESS"],
  IN_PROGRESS: ["WARRANTY", "CLOSED"],
  WARRANTY: ["CLOSED"],
  CLOSED: [],
};

export const canTransitionProject = (from: ProjectStatus, to: ProjectStatus): boolean =>
  PROJECT_TRANSITIONS[from].includes(to);

export const PROJECT_STALE_MESSAGE =
  "그 사이 다른 사람이 현장을 고쳤습니다 — 새로고침한 뒤 다시 해 주세요";

export const PROJECT_ASSIGNEE_MISSING_MESSAGE = "담당자를 찾을 수 없습니다";

export const PROJECT_CREATOR_MISSING_MESSAGE = "작성자를 찾을 수 없습니다";

// 착공 — 착공일 전후 언제든 되지만 계약이 체결 상태여야 한다(설계 결정 1·5).
export const canStart = (p: Pick<Project, "status">, contractStatus: ContractStatus): boolean =>
  canTransitionProject(p.status, "IN_PROGRESS") && contractStatus === "SIGNED";

export const startBlockReason = (
  p: Pick<Project, "status">,
  contractStatus: ContractStatus
): string | null => {
  if (!canTransitionProject(p.status, "IN_PROGRESS")) return "이미 착공한 현장입니다";
  if (contractStatus !== "SIGNED") return "체결 상태인 계약의 현장만 착공할 수 있습니다";
  return null;
};

export const applyStart = (at: string): Pick<Project, "status" | "started_at"> => ({
  status: "IN_PROGRESS",
  started_at: at,
});

// 착공일 변경 — 착공 전 · 계약 체결 상태 · 진행·완료 공정 없음.
export const canReschedule = (
  p: Pick<Project, "status">,
  phases: readonly Pick<ProjectPhase, "status">[],
  contractStatus: ContractStatus
): boolean => rescheduleBlockReason(p, phases, contractStatus) === null;

export const rescheduleBlockReason = (
  p: Pick<Project, "status">,
  phases: readonly Pick<ProjectPhase, "status">[],
  contractStatus: ContractStatus
): string | null => {
  if (p.status !== "SCHEDULED") return "착공 전 현장만 착공일을 바꿀 수 있습니다";
  if (contractStatus !== "SIGNED") return "체결 상태인 계약의 현장만 착공일을 바꿀 수 있습니다";
  if (phases.some((ph) => ph.status === "ACTIVE" || ph.status === "DONE")) {
    return "진행·완료된 공정이 있어 착공일을 바꿀 수 없습니다";
  }
  return null;
};

// 공정 상태 전이 — `0025` 의 project_phase_status_guard 와 같은 표(되돌리기 허용 · ACTIVE 여러 개).
export const PHASE_TRANSITIONS: Record<ProjectPhaseStatus, readonly ProjectPhaseStatus[]> = {
  PENDING: ["ACTIVE", "HOLD"],
  ACTIVE: ["DONE", "HOLD", "PENDING"],
  HOLD: ["ACTIVE", "PENDING"],
  DONE: ["ACTIVE"],
};

export const canChangePhaseStatus = (from: ProjectPhaseStatus, to: ProjectPhaseStatus): boolean =>
  PHASE_TRANSITIONS[from].includes(to);

export const PHASE_STALE_MESSAGE = "그 사이 다른 사람이 공정을 바꿨습니다 — 새로고침한 뒤 다시 해 주세요";

export const PHASE_STATUS_BLOCK = {
  notInProgress: "시공중 현장만 공정 상태를 바꿀 수 있습니다",
  contractNotSigned: "체결 상태인 계약의 현장만 공정 상태를 바꿀 수 있습니다",
  notAllowed: "그 상태로는 바꿀 수 없습니다",
} as const;

// 사유 순서는 `rpc_project_phase_set_status` 와 같다 — 현장 → 계약 → 낡은 화면 → 전이표.
export const phaseStatusBlockReason = (
  p: Pick<Project, "status">,
  contractStatus: ContractStatus,
  phase: Pick<ProjectPhase, "status">,
  from: ProjectPhaseStatus,
  to: ProjectPhaseStatus
): string | null => {
  if (p.status !== "IN_PROGRESS") return PHASE_STATUS_BLOCK.notInProgress;
  if (contractStatus !== "SIGNED") return PHASE_STATUS_BLOCK.contractNotSigned;
  if (phase.status !== from) return PHASE_STALE_MESSAGE;
  if (!canChangePhaseStatus(from, to)) return PHASE_STATUS_BLOCK.notAllowed;
  return null;
};

// 일지·사진을 쓸 수 있는 현장 — 시공중·유지보수(계약이 해지돼도 기록은 남긴다, 결정 3).
export const RECORDABLE_PROJECT_STATUSES: readonly ProjectStatus[] = ["IN_PROGRESS", "WARRANTY"];

// 현장 상태별 고를 수 있는 일지 구분 — 무상 A/S 는 준공 뒤(유지보수)만.
export const logKindsFor = (status: ProjectStatus): readonly ProjectLogKind[] =>
  PROJECT_LOG_KINDS.filter((k) => k !== "WARRANTY_AS" || status === "WARRANTY");

export const LOG_KIND_BLOCK = "무상 A/S 는 유지보수 중인 현장에만 적을 수 있습니다";

export const logKindBlockReason = (p: Pick<Project, "status">, kind: ProjectLogKind): string | null =>
  logKindsFor(p.status).includes(kind) ? null : LOG_KIND_BLOCK;

export const RECORD_BLOCK = {
  notStarted: "착공한 현장만 기록할 수 있습니다",
  closed: "완료된 현장은 기록할 수 없습니다",
} as const;

export const recordBlockReason = (p: Pick<Project, "status">): string | null => {
  if (p.status === "SCHEDULED") return RECORD_BLOCK.notStarted;
  if (p.status === "CLOSED") return RECORD_BLOCK.closed;
  return null;
};

export const LOG_DATE_BLOCK = {
  future: "오늘 이후 날짜에는 쓸 수 없습니다",
  beforeStart: "착공일 전 날짜에는 쓸 수 없습니다",
} as const;

// 기록 날짜는 착공일(KST)~오늘(KST) — 경계는 `build-log.ts logDateBounds` 가 만든다.
export const logDateBlockReason = (
  date: string,
  bounds: { min: string | null; max: string }
): string | null => {
  if (date > bounds.max) return LOG_DATE_BLOCK.future;
  if (bounds.min !== null && date < bounds.min) return LOG_DATE_BLOCK.beforeStart;
  return null;
};

export const RECORD_DELETE_BLOCK = "작성자나 대표만 지울 수 있습니다";

// 일지·사진 지우기 — 작성자 또는 owner(`0025` 의 delete 정책과 같은 규칙).
export const recordDeleteBlockReason = (
  actor: { id: string; role: string },
  authorId: string | null
): string | null => (actor.role === "owner" || (authorId !== null && authorId === actor.id) ? null : RECORD_DELETE_BLOCK);

// 준공 — 시공중 현장만 · 고객 답을 기다리는 변경공사가 없어야 한다(`0027` 의 rpc_project_complete 와 같은 문구).
export const PROJECT_CLOSEOUT_SENT_CHANGE_ORDER_MESSAGE =
  "고객 답을 기다리는 변경공사가 있습니다 — 회수하거나 결정을 받은 뒤 준공해 주세요";

export const CLOSEOUT_BLOCK = {
  notInProgress: "시공중 현장만 준공할 수 있습니다",
  notWarranty: "유지보수 중인 현장만 닫을 수 있습니다",
  future: "오늘 이후 날짜로는 준공할 수 없습니다",
  beforeStart: "착공일 전 날짜로는 준공할 수 없습니다",
} as const;

export const canComplete = (p: Pick<Project, "status">): boolean => p.status === "IN_PROGRESS";

export const canClose = (p: Pick<Project, "status">): boolean => p.status === "WARRANTY";

export const completeBlockReason = (p: Pick<Project, "status">, sentChangeOrders: number): string | null => {
  if (!canComplete(p)) return CLOSEOUT_BLOCK.notInProgress;
  if (sentChangeOrders > 0) return PROJECT_CLOSEOUT_SENT_CHANGE_ORDER_MESSAGE;
  return null;
};

export const closeBlockReason = (p: Pick<Project, "status">, sentChangeOrders: number): string | null => {
  if (!canClose(p)) return CLOSEOUT_BLOCK.notWarranty;
  if (sentChangeOrders > 0) return PROJECT_CLOSEOUT_SENT_CHANGE_ORDER_MESSAGE;
  return null;
};

// 준공일은 착공일(KST)~오늘(KST) — 경계는 기록 날짜와 같은 `logDateBounds` 를 쓴다.
export const completionDateBlockReason = (
  date: string,
  bounds: { min: string | null; max: string }
): string | null => {
  if (date > bounds.max) return CLOSEOUT_BLOCK.future;
  if (bounds.min === null || date < bounds.min) return CLOSEOUT_BLOCK.beforeStart;
  return null;
};

export const unfinishedPhaseConfirmMessage = (count: number): string =>
  `끝나지 않은 공정 ${count}개가 있습니다. 그래도 준공할까요?`;

export const unfinishedPhaseCount = (phases: readonly Pick<ProjectPhase, "status">[]): number =>
  phases.filter((ph) => ph.status !== "DONE").length;

// 완료된 현장은 읽기 전용 — 담당자도 바꾸지 않는다.
export const PROJECT_CLOSED_READONLY_MESSAGE = "완료된 현장은 고칠 수 없습니다";

// 유지보수 종료일 변경 — 유지보수 현장만 · 준공일 이후(`0030` 과 check project_warranty_end 와 같은 문구).
export const WARRANTY_END_BLOCK = {
  notWarranty: "유지보수 중인 현장만 종료일을 바꿀 수 있습니다",
  beforeCompletion: "유지보수 종료일은 준공일 이후여야 합니다",
  tooFar: "유지보수 종료일이 너무 멉니다",
} as const;

// 종료일 상한 — 준공일 + 20년(오타로 2999년이 들어가는 것을 막는다, DB 는 `0033` 이 같은 기준으로 방어).
export const WARRANTY_END_MAX_YEARS = 20;
export const warrantyEndMax = (completionDate: string): string =>
  addMonthsClamped(completionDate, WARRANTY_END_MAX_YEARS * 12);

export const canSetWarrantyEnd = (p: Pick<Project, "status">): boolean => p.status === "WARRANTY";

export const warrantyEndBlockReason = (
  p: Pick<Project, "status" | "completion_date">,
  date: string
): string | null => {
  if (!canSetWarrantyEnd(p)) return WARRANTY_END_BLOCK.notWarranty;
  if (p.completion_date === null || date < p.completion_date) return WARRANTY_END_BLOCK.beforeCompletion;
  if (date > warrantyEndMax(p.completion_date)) return WARRANTY_END_BLOCK.tooFar;
  return null;
};
